import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Check, X, Wrench, ShieldCheck, ArrowUpRight, FileText, Receipt, AlertTriangle, Lock, Image as ImageIcon, ChevronDown, ChevronRight, Star, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { FileUploader, MultiFileUploader } from "../FileUploader";
import { ImpactStoryEditor } from "./Phase3Panels";

const fmtMoney = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s; } };

// W-9 freshness — IRS guidance is to re-collect every 3 years.
// Returns one of: "missing" | "ok" | "expiring" | "expired"
// expiring  = collected 30 - 36 months ago
// expired   = collected > 36 months ago
const w9Status = (c) => {
    const hasDoc = Boolean((c?.w9_document_url || "").trim());
    if (!hasDoc) return "missing";
    if (!c.w9_collected_at) return "ok"; // doc on file, but no timestamp → treat as OK (can't measure)
    const t = new Date(c.w9_collected_at).getTime();
    if (!Number.isFinite(t)) return "ok";
    const monthsOld = (Date.now() - t) / (1000 * 60 * 60 * 24 * 30.4375);
    if (monthsOld >= 36) return "expired";
    if (monthsOld >= 30) return "expiring";
    return "ok";
};

// ---------------- Spend Requests Panel ----------------
export const SpendRequestsPanel = ({ isBoard = false } = {}) => {
    const { user } = useAuth();
    const [requests, setRequests] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [contractors, setContractors] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ campaign_id: "", title: "", purpose_public: "", description: "", amount: "", vendor_name: "", vendor_id: "", requested_by: "", invoice_url: "", invoice_summary_url: "", restriction_acknowledged: false });
    const [balances, setBalances] = useState({});
    const [approveModal, setApproveModal] = useState(null); // { id, title }
    const [approveBody, setApproveBody] = useState({ conflict_disclosed: false, disclosure_notes: "", notes: "" });
    const [expanded, setExpanded] = useState(null); // request id whose details are expanded
    const [completionModal, setCompletionModal] = useState(null); // { id, before_photos, after_photos, completion_notes, completion_proof_url, invoice_summary_url, purpose_public }
    const [impactEditingId, setImpactEditingId] = useState(null);

    const load = async () => {
        const reqs = [
            api.get("/admin/spend-requests"),
            api.get("/campaigns"),
        ];
        if (!isBoard) reqs.push(api.get("/admin/contractors"));
        const results = await Promise.allSettled(reqs);
        const [r, c, k] = results;
        if (r.status === "rejected" && c.status === "rejected") {
            toast.error("Failed to load");
            return;
        }
        setRequests(r.status === "fulfilled" ? (r.value.data || []) : []);
        const camps = c.status === "fulfilled" ? (c.value.data || []) : [];
        setCampaigns(camps);
        setContractors(k?.status === "fulfilled" ? (k.value.data || []) : []);
        const bals = {};
        await Promise.all(camps.map(async (camp) => {
            try {
                const { data } = await api.get(`/admin/campaigns/${camp.id}/balance`);
                bals[camp.id] = data;
            } catch {}
        }));
        setBalances(bals);
    };
    useEffect(() => { load(); }, []);

    const selectedCampaign = campaigns.find((c) => c.id === form.campaign_id);

    const submit = async () => {
        if (!form.campaign_id || !form.title || !form.amount || !form.vendor_name || !form.requested_by) {
            toast.error("Fill campaign, title, amount, vendor, and requestor");
            return;
        }
        if (selectedCampaign?.restricted_fund && !form.restriction_acknowledged) {
            toast.error("Acknowledge the restriction on this fundraiser before filing");
            return;
        }
        try {
            await api.post("/admin/spend-requests", { ...form, amount: parseFloat(form.amount), vendor_id: form.vendor_id || null });
            toast.success("Request filed");
            setCreating(false);
            setForm({ campaign_id: "", title: "", purpose_public: "", description: "", amount: "", vendor_name: "", vendor_id: "", requested_by: "", invoice_url: "", invoice_summary_url: "", restriction_acknowledged: false });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const openApprove = (r) => {
        setApproveModal({ id: r.id, title: r.title, amount: r.amount });
        setApproveBody({ conflict_disclosed: false, disclosure_notes: "", notes: "" });
    };
    const submitApprove = async () => {
        if (approveBody.conflict_disclosed && !approveBody.disclosure_notes.trim()) {
            toast.error("Disclosure notes required when a conflict is disclosed");
            return;
        }
        try {
            const { data } = await api.post(`/admin/spend-requests/${approveModal.id}/approve`, approveBody);
            toast.success(data.needs_more > 0 ? `Approved — needs ${data.needs_more} more sign-off${data.missing_role ? ` (need a ${data.missing_role})` : ""}` : "Fully approved!");
            setApproveModal(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Approve failed");
        }
    };
    const reject = async (id) => {
        const reason = window.prompt("Rejection reason (optional)") || "";
        try { await api.post(`/admin/spend-requests/${id}/reject`, null, { params: { reason } }); toast.success("Rejected"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    };
    const pay = async (id) => {
        if (!window.confirm("Mark this request paid? This will appear on the public ledger.")) return;
        try { await api.post(`/admin/spend-requests/${id}/pay`); toast.success("Paid — added to ledger"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this request?")) return;
        try { await api.delete(`/admin/spend-requests/${id}`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const openCompletion = (r) => {
        setCompletionModal({
            id: r.id,
            before_photos: r.before_photos || [],
            after_photos: r.after_photos || [],
            completion_notes: r.completion_notes || "",
            completion_proof_url: r.completion_proof_url || "",
            invoice_summary_url: r.invoice_summary_url || "",
            purpose_public: r.purpose_public || "",
        });
    };
    const saveCompletion = async () => {
        try {
            await api.patch(`/admin/spend-requests/${completionModal.id}`, {
                before_photos: completionModal.before_photos,
                after_photos: completionModal.after_photos,
                completion_notes: completionModal.completion_notes,
                completion_proof_url: completionModal.completion_proof_url,
                invoice_summary_url: completionModal.invoice_summary_url,
                purpose_public: completionModal.purpose_public,
            });
            toast.success("Public documentation saved");
            setCompletionModal(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    const statusBadge = (s) => ({
        pending: "bg-orange-100 text-orange-700",
        approved: "bg-blue-100 text-blue-700",
        paid: "bg-green-100 text-green-700",
        rejected: "bg-gray-200 text-gray-700",
    }[s] || "bg-gray-100 text-gray-600");

    return (
        <div data-testid="admin-spend-requests-panel" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Spend requests</h2>
                    <p className="text-xs text-[#4A5568] mt-1">Dual sign-off required ({/* server default */}2 approvals) before payment. Paid requests appear on the public ledger.</p>
                </div>
                <button onClick={() => setCreating(true)} data-testid="spend-new-btn" disabled={isBoard} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium ${isBoard ? "hidden" : "bg-[#E53935] hover:bg-[#D32F2F] text-white"}`}>
                    <Plus className="w-4 h-4" strokeWidth={2} /> File new request
                </button>
            </div>

            <ul className="space-y-3" data-testid="spend-list">
                {requests.length === 0 && <li className="text-sm text-[#4A5568] py-10 text-center">No spend requests yet.</li>}
                {requests.map((r) => {
                    const camp = campaigns.find((c) => c.id === r.campaign_id);
                    const bal = balances[r.campaign_id];
                    const alreadyApproved = (r.approvals || []).some((a) => a.email === user?.email);
                    const isExpanded = expanded === r.id;
                    return (
                        <li key={r.id} data-testid={`spend-row-${r.id}`} className="bg-white border border-black/5 rounded-2xl p-5">
                            <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <h3 className="font-heading text-lg text-[#1C252E] font-medium truncate">{r.title}</h3>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${statusBadge(r.status)}`}>
                                            {r.status}
                                        </span>
                                        {camp?.restricted_fund && (
                                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-amber-100 text-amber-800" title={camp.restricted_purpose}>
                                                <Lock className="w-3 h-3" /> Restricted
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-[#4A5568]">
                                        {camp?.name || "—"} · Vendor: <strong>{r.vendor_name}</strong> · Req. by {r.requested_by}
                                    </p>
                                    {r.purpose_public && r.purpose_public !== r.title && (
                                        <p className="text-xs text-[#4A5568] italic mt-1">Public purpose: "{r.purpose_public}"</p>
                                    )}
                                    {r.description && <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{r.description}</p>}
                                    {(r.approvals || []).length > 0 && (
                                        <div className="text-xs text-[#4A5568] mt-2 space-y-0.5">
                                            {(r.approvals || []).map((a, i) => (
                                                <p key={i} className="flex items-center gap-1.5">
                                                    <Check className="w-3 h-3 text-green-600" strokeWidth={2} />
                                                    {a.email} <span className="text-[10px] uppercase tracking-wider bg-[#F4F1EA] px-1.5 py-0.5 rounded">{a.role}</span>
                                                    {a.conflict_disclosed && (
                                                        <span className="inline-flex items-center gap-0.5 text-[10px] text-orange-700 font-bold uppercase tracking-wider" title={a.disclosure_notes}>
                                                            <AlertTriangle className="w-3 h-3" /> Conflict noted
                                                        </span>
                                                    )}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                    {r.rejection_reason && <p className="text-xs text-red-600 mt-2">Rejected: {r.rejection_reason}</p>}
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <p className="font-heading text-2xl text-[#1C252E] font-medium">{fmtMoney(r.amount)}</p>
                                    {bal && <p className="text-xs text-[#4A5568]">available {fmtMoney(bal.available)}</p>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-black/5 mt-3">
                                {r.status === "pending" && (
                                    <>
                                        <button onClick={() => openApprove(r)} disabled={alreadyApproved} data-testid={`spend-approve-${r.id}`}
                                            className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full px-4 py-1.5 text-xs font-medium">
                                            <Check className="w-3.5 h-3.5" strokeWidth={2} /> {alreadyApproved ? "You approved" : "Approve…"}
                                        </button>
                                        <button onClick={() => reject(r.id)} data-testid={`spend-reject-${r.id}`}
                                            className="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-[#1C252E] rounded-full px-4 py-1.5 text-xs font-medium">
                                            <X className="w-3.5 h-3.5" strokeWidth={2} /> Reject
                                        </button>
                                    </>
                                )}
                                {r.status === "approved" && !isBoard && (
                                    <button onClick={() => pay(r.id)} data-testid={`spend-pay-${r.id}`}
                                        className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white rounded-full px-4 py-1.5 text-xs font-medium">
                                        <Receipt className="w-3.5 h-3.5" strokeWidth={2} /> Mark paid → add to ledger
                                    </button>
                                )}
                                {r.status === "approved" && isBoard && (
                                    <span className="text-xs text-[#4A5568] italic" data-testid={`spend-await-pay-${r.id}`}>Awaiting admin payout</span>
                                )}
                                {r.status === "paid" && !isBoard && (
                                    <button onClick={() => openCompletion(r)} data-testid={`spend-completion-${r.id}`}
                                        className="inline-flex items-center gap-1 bg-[#1C252E] hover:bg-black text-white rounded-full px-4 py-1.5 text-xs font-medium">
                                        <ImageIcon className="w-3.5 h-3.5" strokeWidth={2} /> Add public proof
                                    </button>
                                )}
                                {r.status === "paid" && !isBoard && (
                                    <button onClick={() => setImpactEditingId(impactEditingId === r.id ? null : r.id)} data-testid={`spend-impact-${r.id}`}
                                        className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-medium ${r.impact_published ? 'bg-[#E53935] text-white hover:bg-[#D32F2F]' : 'bg-[#F2D8D5] text-[#E53935] hover:bg-[#F2D8D5]/80'}`}>
                                        <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                                        {r.impact_published ? "Edit story" : "Add impact story"}
                                    </button>
                                )}
                                <button onClick={() => setExpanded(isExpanded ? null : r.id)} data-testid={`spend-toggle-${r.id}`}
                                    className="inline-flex items-center gap-1 text-xs text-[#4A5568] hover:text-[#1C252E]">
                                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                    Quotes & details
                                </button>
                                {r.status !== "paid" && !isBoard && (
                                    <button onClick={() => del(r.id)} data-testid={`spend-del-${r.id}`} className="ml-auto p-1.5 rounded-full text-[#E53935] hover:bg-[#F4F1EA]" aria-label="Delete">
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    </button>
                                )}
                                {r.invoice_url && (
                                    <a href={r.invoice_url} target="_blank" rel="noreferrer" className="text-xs text-[#E53935] hover:underline inline-flex items-center gap-1">
                                        <FileText className="w-3 h-3" /> Internal invoice
                                    </a>
                                )}
                            </div>
                            {isExpanded && <QuotesSubpanel requestId={r.id} requestAmount={r.amount} status={r.status} isBoard={isBoard} onChange={load} />}
                            {impactEditingId === r.id && r.status === "paid" && (
                                <ImpactStoryEditor
                                    spendRequest={r}
                                    onClose={() => setImpactEditingId(null)}
                                    onSaved={() => { setImpactEditingId(null); load(); }}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>

            {creating && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)} data-testid="spend-create-modal">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        {/* Sticky header */}
                        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-black/5 flex-shrink-0">
                            <h3 className="font-heading text-xl text-[#1C252E] font-medium">New spend request</h3>
                            <p className="text-xs text-[#4A5568] mt-1">Dual sign-off (admin + board) required before payment · paid requests are public on /ledger.</p>
                        </div>

                        {/* Scrollable body */}
                        <div className="px-6 sm:px-8 py-5 overflow-y-auto flex-1 space-y-6">
                            {/* — Fundraiser / fund — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Fundraiser</h4>
                                <Field label="Source fund *" hint="Where the money will come from. Restricted funds require an extra acknowledgment.">
                                    <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value, restriction_acknowledged: false })} data-testid="spend-form-campaign" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white">
                                        <option value="">Select fundraiser…</option>
                                        {campaigns.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name} ({c.category || "general"}){c.restricted_fund ? " · RESTRICTED" : ""} — available {fmtMoney(balances[c.id]?.available)}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                {selectedCampaign?.restricted_fund && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="spend-form-restriction-banner">
                                        <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1 inline-flex items-center gap-1">
                                            <Lock className="w-3 h-3" /> Restricted fundraiser
                                        </p>
                                        <p className="text-sm text-[#4A5568] mb-3">{selectedCampaign.restricted_purpose || "Donor-restricted purpose applies."}</p>
                                        <label className="flex items-start gap-2 text-xs text-[#1C252E] cursor-pointer">
                                            <input type="checkbox" checked={form.restriction_acknowledged} onChange={(e) => setForm({ ...form, restriction_acknowledged: e.target.checked })} data-testid="spend-form-restriction-ack" className="mt-0.5" />
                                            <span>I confirm this spend matches the approved restriction.</span>
                                        </label>
                                    </div>
                                )}
                            </section>

                            {/* — Basics — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Basics</h4>
                                <Field label="Title *" hint="Internal-only title. Keep it short and specific.">
                                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Boiler repair — middle wing" data-testid="spend-form-title" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                                <Field label="Amount *">
                                    <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" data-testid="spend-form-amount" className="w-full rounded-xl border border-black/10 px-4 py-2.5 font-mono" />
                                </Field>
                                <Field label="Requested by" hint="Who initiated this need (e.g. Principal Smith).">
                                    <input value={form.requested_by} onChange={(e) => setForm({ ...form, requested_by: e.target.value })} data-testid="spend-form-requested-by" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                            </section>

                            {/* — Vendor — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Vendor</h4>
                                <Field label="Linked contractor" hint="Picking a vetted contractor auto-fills the vendor name and is required for IRS 1099-NEC W-9 compliance on paid jobs.">
                                    <select value={form.vendor_id} onChange={(e) => { const c = contractors.find(x => x.id === e.target.value); setForm({ ...form, vendor_id: e.target.value, vendor_name: c?.name || form.vendor_name }); }} data-testid="spend-form-vendor-id" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white">
                                        <option value="">— Not linked / non-1099 payment —</option>
                                        {contractors.filter(c => c.active).map((c) => {
                                            const hasW9 = (c.w9_document_url || "").trim();
                                            return (
                                                <option key={c.id} value={c.id}>
                                                    {c.name} · {c.trade}{hasW9 ? " · W-9 ✓" : " · ⚠ W-9 MISSING"}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </Field>
                                <Field label="Vendor name *" hint="Free text — leave matching the linked contractor if one is selected.">
                                    <input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="spend-form-vendor-name" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                            </section>

                            {/* — Documents — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Documents</h4>
                                <Field label="Internal invoice" hint="Admin-only. Full invoice / quote document.">
                                    <FileUploader value={form.invoice_url} onChange={(url) => setForm({ ...form, invoice_url: url })} label="Upload internal invoice" testid="spend-form-invoice" />
                                </Field>
                                <Field label="Public invoice summary" hint="Donor-safe redacted summary shown on /ledger detail page.">
                                    <FileUploader value={form.invoice_summary_url} onChange={(url) => setForm({ ...form, invoice_summary_url: url })} label="Upload public summary" testid="spend-form-invoice-summary" />
                                </Field>
                                <div className="rounded-xl bg-[#F4F1EA] border border-black/5 p-3">
                                    <p className="text-[11px] text-[#4A5568] leading-relaxed">
                                        Requests over <strong className="font-mono">$500</strong> require at least <strong>2 quotes</strong> attached before approval. Add them via the "Quotes &amp; details" panel on the request after filing.
                                    </p>
                                </div>
                            </section>

                            {/* — Public summary (donor-facing) — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Public summary</h4>
                                <Field label="Public purpose" hint="One-line donor-facing description (max 280 chars). Shown on /ledger.">
                                    <input value={form.purpose_public} onChange={(e) => setForm({ ...form, purpose_public: e.target.value })} maxLength={280} data-testid="spend-form-purpose-public" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                                <Field label="Internal description" hint="Why this is needed, technical details. Not shown publicly.">
                                    <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="spend-form-desc" className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" />
                                </Field>
                            </section>
                        </div>

                        {/* Sticky footer */}
                        <div className="px-6 sm:px-8 py-4 border-t border-black/5 flex items-center justify-end gap-3 flex-shrink-0 bg-[#FDFBF7] rounded-b-3xl">
                            <button onClick={() => setCreating(false)} className="text-sm font-medium text-[#4A5568] hover:text-[#1C252E] px-4 py-2">Cancel</button>
                            <button onClick={submit} data-testid="spend-form-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">File request</button>
                        </div>
                    </div>
                </div>
            )}

            {approveModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setApproveModal(null)} data-testid="spend-approve-modal">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-md w-full my-8 p-6">
                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-1">Approve sign-off</h3>
                        <p className="text-sm text-[#4A5568] mb-4">{approveModal.title} · {fmtMoney(approveModal.amount)}</p>
                        <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-3 inline-flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> Conflict-of-interest disclosure
                                </p>
                                <label className="flex items-start gap-2 text-sm text-[#1C252E] cursor-pointer mb-2">
                                    <input type="checkbox" checked={!approveBody.conflict_disclosed} onChange={() => setApproveBody({ ...approveBody, conflict_disclosed: false, disclosure_notes: "" })} className="mt-1" />
                                    <span>I have NO conflict of interest in this transaction.</span>
                                </label>
                                <label className="flex items-start gap-2 text-sm text-[#1C252E] cursor-pointer">
                                    <input type="checkbox" checked={approveBody.conflict_disclosed} onChange={() => setApproveBody({ ...approveBody, conflict_disclosed: true })} data-testid="spend-approve-conflict-yes" className="mt-1" />
                                    <span>I DO have a conflict to disclose (relationship to vendor, etc).</span>
                                </label>
                                {approveBody.conflict_disclosed && (
                                    <textarea value={approveBody.disclosure_notes} onChange={(e) => setApproveBody({ ...approveBody, disclosure_notes: e.target.value })} placeholder="Describe the conflict (required)" rows={3} data-testid="spend-approve-disclosure-notes" className="w-full mt-3 rounded-xl border border-amber-300 px-3 py-2 text-sm bg-white" />
                                )}
                            </div>
                            <textarea value={approveBody.notes} onChange={(e) => setApproveBody({ ...approveBody, notes: e.target.value })} placeholder="Notes on this approval (optional)" rows={2} data-testid="spend-approve-notes" className="w-full rounded-xl border border-black/10 px-3 py-2 text-sm" />
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-5">
                            <button onClick={() => setApproveModal(null)} className="text-sm font-medium text-[#4A5568]">Cancel</button>
                            <button onClick={submitApprove} data-testid="spend-approve-submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2.5 text-sm font-medium inline-flex items-center gap-1">
                                <Check className="w-4 h-4" /> Submit sign-off
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {completionModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCompletionModal(null)} data-testid="spend-completion-modal">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        {/* Sticky header */}
                        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-black/5 flex-shrink-0">
                            <h3 className="font-heading text-xl text-[#1C252E] font-medium">Add public proof &amp; completion documentation</h3>
                            <p className="text-xs text-[#4A5568] mt-1">
                                These details appear on the public ledger detail page at <code className="bg-[#F4F1EA] px-1 rounded">/ledger/spend/{completionModal.id}</code>.
                            </p>
                        </div>

                        {/* Scrollable body */}
                        <div className="px-6 sm:px-8 py-5 overflow-y-auto flex-1 space-y-6">
                            {/* — Public summary — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Public summary</h4>
                                <Field label="Public purpose" hint="One-line donor-facing description (max 280 chars).">
                                    <input value={completionModal.purpose_public} onChange={(e) => setCompletionModal({ ...completionModal, purpose_public: e.target.value })} maxLength={280} data-testid="completion-purpose" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                                <Field label="Completion notes" hint="What was done, when it was completed, who inspected it. Public.">
                                    <textarea value={completionModal.completion_notes} onChange={(e) => setCompletionModal({ ...completionModal, completion_notes: e.target.value })} rows={4} data-testid="completion-notes" className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" />
                                </Field>
                            </section>

                            {/* — Before / after photos — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Before &amp; after photos</h4>
                                <Field label="Before photos" hint="Drag-drop or upload images showing the original condition.">
                                    <MultiFileUploader values={completionModal.before_photos} onChange={(v) => setCompletionModal({ ...completionModal, before_photos: v })} accept="image/*" label="Add before photo" testid="completion-before" />
                                </Field>
                                <Field label="After photos" hint="Images showing the result. Drives donor trust on the ledger detail page.">
                                    <MultiFileUploader values={completionModal.after_photos} onChange={(v) => setCompletionModal({ ...completionModal, after_photos: v })} accept="image/*" label="Add after photo" testid="completion-after" />
                                </Field>
                            </section>

                            {/* — Documents — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Documents</h4>
                                <Field label="Completion proof" hint="Sign-off PDF, inspector certificate, or similar.">
                                    <FileUploader value={completionModal.completion_proof_url} onChange={(url) => setCompletionModal({ ...completionModal, completion_proof_url: url })} label="Upload proof document" testid="completion-proof" />
                                </Field>
                                <Field label="Public invoice summary" hint="Donor-safe redacted invoice summary.">
                                    <FileUploader value={completionModal.invoice_summary_url} onChange={(url) => setCompletionModal({ ...completionModal, invoice_summary_url: url })} label="Upload public summary" testid="completion-invoice" />
                                </Field>
                            </section>
                        </div>

                        {/* Sticky footer */}
                        <div className="px-6 sm:px-8 py-4 border-t border-black/5 flex items-center justify-end gap-3 flex-shrink-0 bg-[#FDFBF7] rounded-b-3xl">
                            <button onClick={() => setCompletionModal(null)} className="text-sm font-medium text-[#4A5568] hover:text-[#1C252E] px-4 py-2">Cancel</button>
                            <button onClick={saveCompletion} data-testid="completion-save" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">Save public proof</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ---------------- Quotes Subpanel ----------------
const QuotesSubpanel = ({ requestId, requestAmount, status, isBoard, onChange }) => {
    const [quotes, setQuotes] = useState([]);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ vendor_name: "", vendor_contact: "", amount: "", document_url: "", notes: "" });

    const load = async () => {
        try { const { data } = await api.get(`/admin/spend-requests/${requestId}/quotes`); setQuotes(data || []); }
        catch { /* silent */ }
    };
    useEffect(() => { load(); }, [requestId]);

    const submit = async () => {
        if (!form.vendor_name || !form.amount) { toast.error("Vendor and amount required"); return; }
        try {
            await api.post("/admin/quotes", { spend_request_id: requestId, ...form, amount: parseFloat(form.amount) });
            toast.success("Quote attached");
            setAdding(false);
            setForm({ vendor_name: "", vendor_contact: "", amount: "", document_url: "", notes: "" });
            load();
            onChange?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const choose = async (qid) => {
        try { await api.post(`/admin/quotes/${qid}/choose`); toast.success("Marked as chosen"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const del = async (qid) => {
        if (!window.confirm("Delete this quote?")) return;
        try { await api.delete(`/admin/quotes/${qid}`); load(); } catch {}
    };

    const lowest = quotes.length ? Math.min(...quotes.map((q) => q.amount)) : null;
    const needsQuotes = requestAmount > 500 && status === "pending";

    return (
        <div className="mt-4 pt-4 border-t border-dashed border-black/10" data-testid={`quotes-subpanel-${requestId}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#4A5568]">
                    Quotes / bids ({quotes.length})
                    {needsQuotes && quotes.length < 2 && (
                        <span className="ml-2 text-amber-700 normal-case font-normal">— need at least 2 before approval (over $500)</span>
                    )}
                </p>
                {!isBoard && status === "pending" && (
                    <button onClick={() => setAdding(true)} data-testid={`quote-new-btn-${requestId}`} className="text-xs inline-flex items-center gap-1 text-[#E53935] hover:underline">
                        <Plus className="w-3 h-3" /> Attach quote
                    </button>
                )}
            </div>
            {quotes.length === 0 && <p className="text-xs text-[#4A5568] italic">No quotes attached yet.</p>}
            {quotes.length > 0 && (
                <ul className="space-y-2">
                    {quotes.map((q) => (
                        <li key={q.id} data-testid={`quote-row-${q.id}`} className="flex items-center gap-3 bg-[#F4F1EA] rounded-xl px-3 py-2 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#1C252E] inline-flex items-center gap-2">
                                    {q.vendor_name}
                                    {q.is_chosen && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold uppercase tracking-wider"><Star className="w-2.5 h-2.5" /> Chosen</span>}
                                    {q.amount === lowest && !q.is_chosen && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold uppercase tracking-wider">Lowest bid</span>}
                                </p>
                                {q.vendor_contact && <p className="text-[10px] text-[#4A5568]">{q.vendor_contact}</p>}
                                {q.notes && <p className="text-[11px] text-[#4A5568] italic mt-1">{q.notes}</p>}
                            </div>
                            <p className="font-mono font-medium text-[#1C252E]">{fmtMoney(q.amount)}</p>
                            {q.document_url && <a href={q.document_url} target="_blank" rel="noreferrer" className="text-xs text-[#E53935] hover:underline inline-flex items-center gap-0.5"><FileText className="w-3 h-3" /> Doc</a>}
                            {!isBoard && status === "pending" && (
                                <>
                                    {!q.is_chosen && <button onClick={() => choose(q.id)} data-testid={`quote-choose-${q.id}`} className="text-xs text-blue-600 hover:underline">Choose</button>}
                                    <button onClick={() => del(q.id)} data-testid={`quote-del-${q.id}`} className="p-1 hover:bg-black/5 rounded" aria-label="Delete">
                                        <Trash2 className="w-3 h-3 text-[#E53935]" />
                                    </button>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {adding && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setAdding(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-md w-full my-8 p-6">
                        <h3 className="font-heading text-lg text-[#1C252E] font-medium mb-4">Attach a quote / bid</h3>
                        <div className="space-y-3">
                            <input placeholder="Vendor name" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="quote-form-vendor" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input placeholder="Contact (email/phone)" value={form.vendor_contact} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input type="number" step="0.01" placeholder="Quote amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="quote-form-amount" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <textarea placeholder="Notes (scope, exclusions, etc)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <FileUploader value={form.document_url} onChange={(url) => setForm({ ...form, document_url: url })} label="Upload quote document (PDF/image)" testid="quote-form-doc" />
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-5">
                            <button onClick={() => setAdding(false)} className="text-sm font-medium text-[#4A5568]">Cancel</button>
                            <button onClick={submit} data-testid="quote-form-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">Attach quote</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ---------------- Contractor Card (full info) ----------------
const ContractorCard = ({ c, readOnly, onDelete, onEdit }) => {
    const [open, setOpen] = useState(false);
    const name = c.name || c.business_name || "(unnamed)";
    const hasVettingDetails = c.contact_name || c.insurance_carrier || c.years_in_business || c.references || c.coi_disclosure || c.pricing_notes || c.why_us || c.vetted_at;
    const w9 = w9Status(c);
    return (
        <li data-testid={`contractor-row-${c.id}`} className="bg-white border border-black/5 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading text-lg text-[#1C252E] font-medium">{name}</h3>
                        {c.vetted && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-testid={`contractor-vetted-${c.id}`}>
                                <ShieldCheck className="w-3 h-3" strokeWidth={2} /> Vetted
                            </span>
                        )}
                        {w9 === "ok" && (
                            <span className="text-[10px] uppercase tracking-wider font-bold bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-testid={`contractor-w9-ok-${c.id}`} title={c.w9_collected_at ? `W-9 on file since ${fmtDate(c.w9_collected_at)}` : "W-9 on file"}>
                                W-9 ✓
                            </span>
                        )}
                        {w9 === "expiring" && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full" data-testid={`contractor-w9-expiring-${c.id}`} title={`W-9 was collected ${fmtDate(c.w9_collected_at)} — IRS recommends re-collecting every 3 years`}>
                                <AlertTriangle className="w-3 h-3" strokeWidth={2} /> W-9 expiring
                            </span>
                        )}
                        {w9 === "expired" && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid={`contractor-w9-expired-${c.id}`} title={`W-9 was collected ${fmtDate(c.w9_collected_at)} — older than 3 years, please re-collect before paying`}>
                                <AlertTriangle className="w-3 h-3" strokeWidth={2} /> W-9 expired
                            </span>
                        )}
                        {w9 === "missing" && (
                            <span className="text-[10px] uppercase tracking-wider font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid={`contractor-w9-missing-${c.id}`} title="No W-9 on file — payments will be blocked">
                                W-9 missing
                            </span>
                        )}
                        {c.active === false && (
                            <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Inactive</span>
                        )}
                    </div>
                    <p className="text-xs text-[#4A5568] uppercase tracking-wider mt-1 inline-flex items-center gap-1.5">
                        <Wrench className="w-3 h-3" /> {c.trade}
                        {c.rating ? <span className="ml-2 text-[#E53935]">{"★".repeat(c.rating)}</span> : null}
                        {c.years_in_business ? <span className="ml-2 text-[#4A5568] normal-case">· {c.years_in_business} yrs in business</span> : null}
                    </p>
                </div>
                {!readOnly && (
                    <div className="flex items-center gap-1">
                        {onEdit && (
                            <button onClick={() => onEdit(c)} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Edit" data-testid={`contractor-edit-${c.id}`}>
                                <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        )}
                        <button onClick={() => onDelete(c.id)} className="p-1.5 rounded-full text-[#E53935] hover:bg-[#F4F1EA]" aria-label="Delete"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                )}
            </div>
            <dl className="text-xs text-[#4A5568] space-y-0.5 mt-2">
                {c.contact_name && <div><dt className="inline font-bold">Contact: </dt><dd className="inline">{c.contact_name}</dd></div>}
                {c.contact_email && <div><dt className="inline font-bold">Email: </dt><dd className="inline">{c.contact_email}</dd></div>}
                {c.contact_phone && <div><dt className="inline font-bold">Phone: </dt><dd className="inline">{c.contact_phone}</dd></div>}
                {c.service_area && <div><dt className="inline font-bold">Service area: </dt><dd className="inline">{c.service_area}</dd></div>}
                {c.emergency_availability && c.emergency_availability !== "none" && (
                    <div><dt className="inline font-bold">Availability: </dt><dd className="inline">{_EMERG_LABELS[c.emergency_availability] || c.emergency_availability}</dd></div>
                )}
                {c.license_number && <div><dt className="inline font-bold">License: </dt><dd className="inline font-mono">{c.license_number}</dd></div>}
                {c.license_document_url && <div><a href={c.license_document_url} target="_blank" rel="noreferrer" className="text-[#E53935] hover:underline inline-flex items-center gap-1"><FileText className="w-3 h-3" /> License / certification</a></div>}
                {c.insurance_carrier && <div><dt className="inline font-bold">Insurance carrier: </dt><dd className="inline">{c.insurance_carrier}</dd></div>}
                {c.insurance_expiry && <div><dt className="inline font-bold">Insurance expires: </dt><dd className="inline">{c.insurance_expiry}</dd></div>}
                {c.insurance_document_url && <div><a href={c.insurance_document_url} target="_blank" rel="noreferrer" className="text-[#E53935] hover:underline inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Insurance document</a></div>}
                {c.w9_document_url && <div><a href={c.w9_document_url} target="_blank" rel="noreferrer" className="text-[#E53935] hover:underline inline-flex items-center gap-1"><FileText className="w-3 h-3" /> W-9 on file{c.w9_collected_at ? ` · ${fmtDate(c.w9_collected_at)}` : ""}</a></div>}
                {c.address && <div><dt className="inline font-bold">Address: </dt><dd className="inline">{c.address}</dd></div>}
                <div><dt className="inline font-bold">Jobs done: </dt><dd className="inline">{c.jobs_count ?? 0} · {fmtMoney(c.total_paid)} total</dd></div>
                {c.vetted_at && <div className="text-[10px]"><dt className="inline font-bold">Vetted: </dt><dd className="inline">{fmtDate(c.vetted_at)}{c.vetted_by ? ` by ${c.vetted_by}` : ""}</dd></div>}
            </dl>
            {hasVettingDetails && (
                <button onClick={() => setOpen((o) => !o)} data-testid={`contractor-toggle-${c.id}`} className="mt-3 text-[11px] uppercase tracking-wider font-bold text-[#E53935] inline-flex items-center gap-1 hover:underline">
                    {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {open ? "Hide" : "Show"} vetting details
                </button>
            )}
            {open && hasVettingDetails && (
                <div className="mt-3 pt-3 border-t border-black/5 text-xs text-[#4A5568] space-y-2" data-testid={`contractor-details-${c.id}`}>
                    {c.references && <div><p className="font-bold text-[#1C252E] mb-0.5">References</p><p className="whitespace-pre-line">{c.references}</p></div>}
                    {c.coi_disclosure && <div><p className="font-bold text-[#1C252E] mb-0.5">Conflict-of-interest disclosure</p><p className="whitespace-pre-line">{c.coi_disclosure}</p></div>}
                    {c.pricing_notes && <div><p className="font-bold text-[#1C252E] mb-0.5">Pricing notes</p><p className="whitespace-pre-line">{c.pricing_notes}</p></div>}
                    {c.why_us && <div><p className="font-bold text-[#1C252E] mb-0.5">Why work with Reddog</p><p className="whitespace-pre-line">{c.why_us}</p></div>}
                    {c.notes && <div><p className="font-bold text-[#1C252E] mb-0.5">Internal notes</p><p className="italic whitespace-pre-line">{c.notes}</p></div>}
                </div>
            )}
        </li>
    );
};

const _EMERG_LABELS = {
    none: "No emergency / off-hours",
    business_hours: "Business hours only",
    weekends_only: "Weekends only",
    "24_7": "24/7 emergency",
};

// Tiny labeled-field wrapper used inside the contractor modal so every input
// has a visible label + optional hint (replacing placeholder-only fields).
const Field = ({ label, hint, children }) => (
    <div>
        <label className="block text-[11px] uppercase tracking-wider font-bold text-[#4A5568] mb-1.5">{label}</label>
        {children}
        {hint && <p className="text-[11px] text-[#4A5568] mt-1 leading-snug">{hint}</p>}
    </div>
);

const _emptyContractor = {
    name: "", trade: "",
    license_number: "", license_document_url: "",
    insurance_expiry: "", insurance_document_url: "",
    w9_document_url: "",
    service_area: "", emergency_availability: "none",
    contact_email: "", contact_phone: "", address: "", rating: 5, notes: "",
};

// ---------------- Contractors Panel ----------------
export const ContractorsPanel = ({ readOnly = false } = {}) => {
    const [items, setItems] = useState([]);
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(null); // {id, ...form}
    const [form, setForm] = useState(_emptyContractor);
    const [filter, setFilter] = useState("all"); // all | needs_attention

    const load = async () => { try { const { data } = await api.get("/admin/contractors"); setItems(data || []); } catch { toast.error("Failed to load"); } };
    useEffect(() => { load(); }, []);

    // W-9 freshness rollup — flag anything expiring soon or older than 3 years
    const w9Summary = items.reduce((acc, c) => {
        const s = w9Status(c);
        acc[s] = (acc[s] || 0) + 1;
        return acc;
    }, { ok: 0, expiring: 0, expired: 0, missing: 0 });
    const needsAttention = (w9Summary.expiring || 0) + (w9Summary.expired || 0) + (w9Summary.missing || 0);

    const visible = filter === "needs_attention"
        ? items.filter((c) => w9Status(c) !== "ok")
        : items;

    const save = async () => {
        if (!form.name || !form.trade) { toast.error("Name and trade required"); return; }
        try {
            const payload = { ...form, rating: parseInt(form.rating, 10) || null, contact_email: form.contact_email || null };
            if (editing) {
                await api.patch(`/admin/contractors/${editing}`, payload);
                toast.success("Contractor updated");
            } else {
                await api.post("/admin/contractors", payload);
                toast.success("Contractor added");
            }
            setAdding(false);
            setEditing(null);
            setForm(_emptyContractor);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const openEdit = (c) => {
        setForm({
            name: c.name || "", trade: c.trade || "",
            license_number: c.license_number || "", license_document_url: c.license_document_url || "",
            insurance_expiry: c.insurance_expiry || "", insurance_document_url: c.insurance_document_url || "",
            w9_document_url: c.w9_document_url || "",
            service_area: c.service_area || "", emergency_availability: c.emergency_availability || "none",
            contact_email: c.contact_email || "", contact_phone: c.contact_phone || "",
            address: c.address || "", rating: c.rating || 5, notes: c.notes || "",
        });
        setEditing(c.id);
        setAdding(true);
    };
    const del = async (id) => { if (!window.confirm("Delete contractor?")) return; await api.delete(`/admin/contractors/${id}`); load(); };

    return (
        <div data-testid="admin-contractors-panel" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Approved contractors</h2>
                    <p className="text-xs text-[#4A5568] mt-1">Private vetted vendor directory. Link from spend requests.</p>
                </div>
                <button onClick={() => setAdding(true)} data-testid="contractor-new-btn" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium ${readOnly ? "hidden" : "bg-[#E53935] hover:bg-[#D32F2F] text-white"}`}>
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add contractor
                </button>
            </div>

            {/* W-9 freshness banner — surfaces expiring / expired W-9s before payouts are blocked */}
            {items.length > 0 && needsAttention > 0 && (
                <div className="rounded-2xl border border-amber-300 bg-amber-50/80 px-5 py-4" data-testid="w9-attention-banner">
                    <div className="flex items-start gap-3 flex-wrap">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-amber-700" strokeWidth={1.8} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#1C252E]">
                                {needsAttention} contractor{needsAttention === 1 ? "" : "s"} need{needsAttention === 1 ? "s" : ""} W-9 attention
                            </p>
                            <p className="text-xs text-[#4A5568] mt-1 leading-relaxed">
                                IRS guidance is to re-collect Form W-9 every <strong>3 years</strong>. Payouts to contractors with an expired or missing W-9 are blocked at the spend-request stage.
                            </p>
                            <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px]">
                                {w9Summary.expired > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-bold uppercase tracking-wider" data-testid="w9-summary-expired">
                                        {w9Summary.expired} expired
                                    </span>
                                )}
                                {w9Summary.expiring > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-200 text-amber-900 font-bold uppercase tracking-wider" data-testid="w9-summary-expiring">
                                        {w9Summary.expiring} expiring soon
                                    </span>
                                )}
                                {w9Summary.missing > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-bold uppercase tracking-wider" data-testid="w9-summary-missing">
                                        {w9Summary.missing} missing
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => setFilter(filter === "needs_attention" ? "all" : "needs_attention")}
                            data-testid="w9-filter-toggle"
                            className="text-xs font-bold uppercase tracking-wider text-amber-900 hover:underline whitespace-nowrap"
                        >
                            {filter === "needs_attention" ? "Show all" : "Show only these"}
                        </button>
                    </div>
                </div>
            )}

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="contractor-list">
                {visible.length === 0 && (
                    <li className="col-span-2 text-sm text-[#4A5568] py-10 text-center">
                        {filter === "needs_attention" ? "No contractors currently need W-9 attention. Nice work." : "No contractors registered yet."}
                    </li>
                )}
                {visible.map((c) => <ContractorCard key={c.id} c={c} readOnly={readOnly} onDelete={del} onEdit={openEdit} />)}
            </ul>

            {adding && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setAdding(false); setEditing(null); setForm(_emptyContractor); }}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        {/* Sticky header */}
                        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-black/5 flex-shrink-0">
                            <h3 className="font-heading text-xl text-[#1C252E] font-medium" data-testid="contractor-form-title">
                                {editing ? "Edit contractor" : "Add contractor"}
                            </h3>
                            <p className="text-xs text-[#4A5568] mt-1">Private vetted vendor registry · all fields except company name + trade are optional</p>
                        </div>

                        {/* Scrollable body, grouped into sections */}
                        <div className="px-6 sm:px-8 py-5 overflow-y-auto flex-1 space-y-6">
                            {/* — Basics — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Basics</h4>
                                <Field label="Company name *">
                                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contractor-form-name" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                                <Field label="Trade *" hint="Plumbing, HVAC, electrician, carpentry, excavation, etc.">
                                    <input value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} data-testid="contractor-form-trade" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                            </section>

                            {/* — License & certification — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">License &amp; certification</h4>
                                <Field label="License number">
                                    <input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                                <Field label="License / certification document" hint="PDF or image">
                                    <FileUploader value={form.license_document_url} onChange={(url) => setForm({ ...form, license_document_url: url })} label="Upload license / cert" testid="contractor-form-license-doc" />
                                </Field>
                            </section>

                            {/* — Insurance — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Insurance</h4>
                                <Field label="Insurance expiry" hint="YYYY-MM-DD format">
                                    <input value={form.insurance_expiry} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} placeholder="2026-12-31" className="w-full rounded-xl border border-black/10 px-4 py-2.5 font-mono" />
                                </Field>
                                <Field label="Insurance document">
                                    <FileUploader value={form.insurance_document_url} onChange={(url) => setForm({ ...form, insurance_document_url: url })} label="Upload insurance document" testid="contractor-form-insurance-doc" />
                                </Field>
                            </section>

                            {/* — IRS W-9 (highlighted) — */}
                            <section className="rounded-xl bg-[#FFF5F4] border border-[#E53935]/30 p-4 space-y-2">
                                <div className="flex items-baseline gap-2">
                                    <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">IRS Form W-9</h4>
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#E53935]">*required before payment</span>
                                </div>
                                <p className="text-[11px] text-[#4A5568] leading-relaxed">
                                    No spend request can be marked paid to this contractor without a W-9 on file.
                                    For volunteer reimbursements or non-1099 vendors, set <code className="bg-white px-1 rounded">bypass_w9_reason</code> on the spend request instead.
                                </p>
                                <FileUploader value={form.w9_document_url} onChange={(url) => setForm({ ...form, w9_document_url: url })} label="Upload signed W-9" testid="contractor-form-w9" />
                            </section>

                            {/* — Service & availability — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Service &amp; availability</h4>
                                <Field label="Service area" hint="Where do you work? Helps match jobs to nearby contractors.">
                                    <input value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} placeholder="e.g. Frenchtown + within 15 miles" data-testid="contractor-form-service-area" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                                <Field label="Emergency / off-hours availability">
                                    <select value={form.emergency_availability} onChange={(e) => setForm({ ...form, emergency_availability: e.target.value })} data-testid="contractor-form-emergency" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white">
                                        {Object.entries(_EMERG_LABELS).map(([slug, lbl]) => <option key={slug} value={slug}>{lbl}</option>)}
                                    </select>
                                </Field>
                            </section>

                            {/* — Contact — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Contact</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="Email">
                                        <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                    </Field>
                                    <Field label="Phone">
                                        <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                    </Field>
                                </div>
                                <Field label="Address">
                                    <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                            </section>

                            {/* — Internal — */}
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Internal</h4>
                                <Field label="Rating">
                                    <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white">
                                        <option value="">— None —</option>{[1,2,3,4,5].map(n => <option key={n} value={n}>{"★".repeat(n)} ({n} star{n>1?"s":""})</option>)}
                                    </select>
                                </Field>
                                <Field label="Internal notes" hint="Past jobs, quality observations, anything board-only.">
                                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" />
                                </Field>
                            </section>
                        </div>

                        {/* Sticky footer */}
                        <div className="px-6 sm:px-8 py-4 border-t border-black/5 flex items-center justify-end gap-3 flex-shrink-0 bg-[#FDFBF7] rounded-b-3xl">
                            <button onClick={() => { setAdding(false); setEditing(null); setForm(_emptyContractor); }} className="text-sm font-medium text-[#4A5568] hover:text-[#1C252E] px-4 py-2">Cancel</button>
                            <button onClick={save} data-testid="contractor-form-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">{editing ? "Update contractor" : "Save contractor"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ---------------- Credentials Panel ----------------
export const CredentialsPanel = ({ readOnly = false } = {}) => {
    const [items, setItems] = useState([]);
    const [volunteers, setVolunteers] = useState([]);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ volunteer_id: "", trade: "", license_number: "", license_issuer: "", license_expiry: "", document_url: "", notes: "" });

    const load = async () => {
        try {
            const [c, v] = await Promise.all([
                api.get("/admin/credentials"),
                api.get("/volunteers"),
            ]);
            setItems(c.data || []);
            setVolunteers(v.data || []);
        } catch { toast.error("Failed to load"); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.volunteer_id || !form.trade) { toast.error("Volunteer and trade required"); return; }
        try { await api.post("/admin/credentials", form); toast.success("Credential filed"); setAdding(false); setForm({ volunteer_id: "", trade: "", license_number: "", license_issuer: "", license_expiry: "", document_url: "", notes: "" }); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const verify = async (id) => { try { await api.post(`/admin/credentials/${id}/verify`); toast.success("Verified"); load(); } catch { toast.error("Failed"); } };
    const unverify = async (id) => { try { await api.post(`/admin/credentials/${id}/unverify`); load(); } catch {} };
    const promote = async (id) => {
        if (!window.confirm("Promote this verified volunteer to an approved contractor?")) return;
        try { await api.post(`/admin/credentials/${id}/promote-to-contractor`); toast.success("Promoted to contractor"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete credential?")) return; try { await api.delete(`/admin/credentials/${id}`); load(); } catch {} };

    return (
        <div data-testid="admin-credentials-panel" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Volunteer credentials</h2>
                    <p className="text-xs text-[#4A5568] mt-1">Verified pro skills inside the community. Promote a verified credential to the approved-contractor list with one click.</p>
                </div>
                <button onClick={() => setAdding(true)} data-testid="cred-new-btn" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium ${readOnly ? "hidden" : "bg-[#E53935] hover:bg-[#D32F2F] text-white"}`}>
                    <Plus className="w-4 h-4" strokeWidth={2} /> File credential
                </button>
            </div>

            <ul className="space-y-3" data-testid="cred-list">
                {items.length === 0 && <li className="text-sm text-[#4A5568] py-10 text-center">No credentials filed yet.</li>}
                {items.map((c) => (
                    <li key={c.id} data-testid={`cred-row-${c.id}`} className="bg-white border border-black/5 rounded-2xl p-5">
                        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <h3 className="font-heading text-lg text-[#1C252E] font-medium">{c.volunteer_name}</h3>
                                    {c.verified ? (
                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-green-100 text-green-700">
                                            <ShieldCheck className="w-3 h-3" strokeWidth={2} /> Verified
                                        </span>
                                    ) : (
                                        <span className="text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-orange-100 text-orange-700">Pending</span>
                                    )}
                                </div>
                                <p className="text-xs text-[#4A5568]">{c.volunteer_email}</p>
                                <p className="text-sm text-[#1C252E] mt-2"><strong>{c.trade}</strong>{c.license_issuer && ` · ${c.license_issuer}`}</p>
                                {c.license_number && <p className="text-xs font-mono text-[#4A5568]">License: {c.license_number}{c.license_expiry && ` · expires ${c.license_expiry}`}</p>}
                                {c.notes && <p className="text-xs text-[#4A5568] mt-2 italic">{c.notes}</p>}
                            </div>
                            <button onClick={() => del(c.id)} className={`p-1.5 rounded-full text-[#E53935] hover:bg-[#F4F1EA] ${readOnly ? "hidden" : ""}`} aria-label="Delete"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-black/5 mt-3">
                            {!c.verified ? (
                                <button onClick={() => verify(c.id)} data-testid={`cred-verify-${c.id}`} disabled={readOnly} className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-medium ${readOnly ? "hidden" : "bg-green-600 hover:bg-green-700 text-white"}`}>
                                    <Check className="w-3.5 h-3.5" strokeWidth={2} /> Mark verified
                                </button>
                            ) : (
                                <>
                                    <button onClick={() => promote(c.id)} data-testid={`cred-promote-${c.id}`} disabled={readOnly} className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-medium ${readOnly ? "hidden" : "bg-[#1C252E] hover:bg-black text-white"}`}>
                                        <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} /> Promote to contractor
                                    </button>
                                    {!readOnly && <button onClick={() => unverify(c.id)} className="text-xs text-[#4A5568] hover:text-[#E53935]">Un-verify</button>}
                                </>
                            )}
                            {c.document_url && <a href={c.document_url} target="_blank" rel="noreferrer" className="ml-auto text-xs text-[#E53935] hover:underline inline-flex items-center gap-1"><FileText className="w-3 h-3" /> View doc</a>}
                            {c.verified_at && <span className="text-[10px] text-[#4A5568] ml-auto">Verified {fmtDate(c.verified_at)} by {c.verified_by}</span>}
                        </div>
                    </li>
                ))}
            </ul>

            {adding && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAdding(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        {/* Sticky header */}
                        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-black/5 flex-shrink-0">
                            <h3 className="font-heading text-xl text-[#1C252E] font-medium">File credential for a volunteer</h3>
                            <p className="text-xs text-[#4A5568] mt-1">Private credentials vault · only visible to admins with the appropriate permission.</p>
                        </div>

                        {/* Body */}
                        <div className="px-6 sm:px-8 py-5 overflow-y-auto flex-1 space-y-6">
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Volunteer &amp; trade</h4>
                                <Field label="Volunteer *" hint="Who holds this credential. Pick from the active volunteer list.">
                                    <select value={form.volunteer_id} onChange={(e) => setForm({ ...form, volunteer_id: e.target.value })} data-testid="cred-form-volunteer" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white">
                                        <option value="">Pick a volunteer…</option>
                                        {volunteers.map((v) => <option key={v.id} value={v.id}>{v.name} — {v.email}</option>)}
                                    </select>
                                </Field>
                                <Field label="Trade *" hint="Plumbing, electrical, boiler, HVAC, food handling, etc.">
                                    <input value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} data-testid="cred-form-trade" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                            </section>

                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">License details</h4>
                                <Field label="License number">
                                    <input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5 font-mono" />
                                </Field>
                                <Field label="Issuer" hint="e.g. State Board of Electrical Examiners, NJ DOH, etc.">
                                    <input value={form.license_issuer} onChange={(e) => setForm({ ...form, license_issuer: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </Field>
                                <Field label="Expiry" hint="YYYY-MM-DD. Used to flag expiring credentials in the dashboard.">
                                    <input value={form.license_expiry} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} placeholder="2027-08-15" className="w-full rounded-xl border border-black/10 px-4 py-2.5 font-mono" />
                                </Field>
                                <Field label="License / cert document" hint="PDF or image. Stored privately — never on the public ledger.">
                                    <FileUploader value={form.document_url} onChange={(url) => setForm({ ...form, document_url: url })} label="Upload license document" testid="cred-form-doc" />
                                </Field>
                            </section>

                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Internal</h4>
                                <Field label="Notes" hint="Verification source, prior projects, anything board-only.">
                                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" />
                                </Field>
                            </section>
                        </div>

                        {/* Sticky footer */}
                        <div className="px-6 sm:px-8 py-4 border-t border-black/5 flex items-center justify-end gap-3 flex-shrink-0 bg-[#FDFBF7] rounded-b-3xl">
                            <button onClick={() => setAdding(false)} className="text-sm font-medium text-[#4A5568] hover:text-[#1C252E] px-4 py-2">Cancel</button>
                            <button onClick={save} data-testid="cred-form-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">Save credential</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
